/**
 * The one register of "the portal is waiting on something".
 *
 * Same holder pattern as `navigation.ts` and `collabStore.ts`: module state
 * behind `useSyncExternalStore`, so any layer that starts a slow operation can
 * announce it without threading props through the screens. `LoadingOverlay`
 * (mounted once, in `Shell`) is the only reader.
 *
 * Jobs are counted, not toggled: two overlapping fetches must not have the
 * first one's completion hide the overlay while the second is still running.
 * The message shown is the most recent job's — the thing the user just did is
 * the thing to narrate.
 */
import { useSyncExternalStore } from 'react'

import type { MessageKey } from '../shell/i18n.js'

export interface LoadingState {
  readonly active: boolean
  /** Key into en.json's `loading.*` family — never a literal. */
  readonly message: MessageKey
}

interface Job {
  readonly id: number
  readonly message: MessageKey
}

let jobs: readonly Job[] = []
let nextId = 1
let snapshot: LoadingState = { active: false, message: 'loading.generic' }
const listeners = new Set<() => void>()

function publish(): void {
  const last = jobs.at(-1)
  snapshot =
    last === undefined
      ? { active: false, message: snapshot.message }
      : { active: true, message: last.message }
  for (const listener of listeners) listener()
}

/** Announce a slow operation. Returns the matching `end`; always call it in `finally`. */
export function beginLoading(message: MessageKey): () => void {
  const id = nextId++
  jobs = [...jobs, { id, message }]
  publish()

  return () => {
    jobs = jobs.filter((job) => job.id !== id)
    publish()
  }
}

/** The usual shape: wrap the promise, guarantee the `end`. */
export async function withLoading<T>(message: MessageKey, work: () => Promise<T>): Promise<T> {
  const end = beginLoading(message)
  try {
    return await work()
  } finally {
    end()
  }
}

/** The current state, for non-hook readers and tests. */
export function loadingSnapshot(): LoadingState {
  return snapshot
}

export function useLoading(): LoadingState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)

      return () => listeners.delete(listener)
    },
    () => snapshot,
  )
}

/** Test hook: forget every job. */
export function resetLoading(): void {
  jobs = []
  publish()
}
